/**
 * Componente PlanCard
 * Tarjeta para mostrar planes de suscripción
 */

import React, { useState } from 'react';
import { 
  SUBSCRIPTION_STATUS_DISPLAY, 
  SUPPORTED_CURRENCIES 
} from '../../constants/subscriptions';

/**
 * Tarjeta de plan de suscripción
 * @param {Object} plan - Datos del plan
 * @param {Object} userSubscription - Suscripción actual del usuario
 * @param {Function} onSubscribe - Función al suscribirse
 * @param {boolean} loading - Estado de carga
 * @param {boolean} popular - Si es el plan popular
 * @param {boolean} recommended - Si es recomendado
 */
const PlanCard = ({ 
  plan, 
  userSubscription, 
  onSubscribe, 
  loading = false,
  popular = false,
  recommended = false 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  /**
   * Verifica si el usuario ya tiene este plan
   */
  const isCurrentPlan = userSubscription?.plan_id === plan.id;

  /**
   * Verifica si el usuario puede suscribirse a este plan
   */
  const canSubscribe = !userSubscription || !isCurrentPlan;

  /**
   * Obtiene el texto del botón
   */
  const getButtonText = () => {
    if (loading) return 'Procesando...';
    if (isCurrentPlan) return 'Plan Actual';
    if (userSubscription) return 'Cambiar Plan';
    return 'Suscribirse';
  };

  /**
   * Obtiene el color del botón
   */
  const getButtonColor = () => {
    if (isCurrentPlan) return 'bg-gray-100 text-gray-700 cursor-not-allowed';
    if (popular) return 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700';
    return 'bg-blue-600 hover:bg-blue-700';
  };

  /**
   * Formatea el precio
   */
  const formatPrice = (price, currency) => {
    const currencyInfo = SUPPORTED_CURRENCIES[currency] || SUPPORTED_CURRENCIES.MXN;
    return `${currencyInfo.symbol}${price.toFixed(2)}`;
  };

  /**
   * Maneja el clic en el botón
   */
  const handleSubscribeClick = () => {
    if (!canSubscribe || loading) return;
    onSubscribe(plan);
  };

  return (
    <div 
      className={`
        relative bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300
        ${popular ? 'border-purple-500 shadow-xl' : 'border-gray-200'}
        ${isHovered ? 'transform -translate-y-1 shadow-2xl' : ''}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Badges */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        {popular && (
          <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full">
            POPULAR
          </span>
        )}
        {recommended && (
          <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            RECOMENDADO
          </span>
        )}
      </div>

      {/* Header */}
      <div className={`p-6 ${popular ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-gray-50'}`}>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.plan_name || plan.nombre}</h3>
        <div className="flex items-baseline gap-2">
          {/* Muestra precio promocional si existe, de lo contrario muestra precio normal */}
          {/* El precio promocional se destaca en verde y el normal aparece tachado */}
          {(plan.plan_price_promo || plan.plan_price_promo === 0) ? (
            <>
              <span className="text-4xl font-bold text-green-600">
                {formatPrice(plan.plan_price_promo, plan.moneda)}
              </span>
              <span className="text-gray-500 line-through text-lg">
                {formatPrice(plan.plan_price || plan.precio, plan.moneda)}
              </span>
              <span className="text-gray-600">/{plan.frecuencia}</span>
            </>
          ) : (
            <>
              <span className="text-4xl font-bold text-gray-900">
                {formatPrice(plan.plan_price || plan.precio, plan.moneda)}
              </span>
              <span className="text-gray-600">/{plan.frecuencia}</span>
            </>
          )}
        </div>
        {plan.descripcion && (
          <p className="text-gray-600 mt-3 text-sm">{plan.descripcion}</p>
        )}
      </div>

      {/* Features */}
      <div className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Características</h4>
        <ul className="space-y-3">
          {plan.plan_features?.map((planFeature, index) => {
            const feature = planFeature.features;
            if (!feature) return null;
            
            return (
              <li key={index} className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  feature.feature_active 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {feature.feature_active ? (
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className={`font-medium ${feature.feature_active ? 'text-gray-900' : 'text-gray-400'}`}>
                    {feature.feature_name}
                  </p>
                  {feature.feature_desc && (
                    <p className={`text-sm ${feature.feature_active ? 'text-gray-600' : 'text-gray-400'}`}>
                      {feature.feature_desc}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Footer */}
      <div className="p-6 bg-gray-50 border-t border-gray-200">
        <button
          onClick={handleSubscribeClick}
          disabled={!canSubscribe || loading}
          className={`
            w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200
            ${getButtonColor()}
            ${canSubscribe && !loading ? 'transform hover:scale-105' : ''}
          `}
        >
          {getButtonText()}
        </button>

        {/* Estado actual */}
        {isCurrentPlan && userSubscription && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">
                {SUBSCRIPTION_STATUS_DISPLAY[userSubscription.estado]}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Renovación: {new Date(userSubscription.proxima_renovacion).toLocaleDateString('es-MX')}
            </p>
          </div>
        )}

        {/* Información adicional */}
        {!isCurrentPlan && userSubscription && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Cambiará tu plan actual por este
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
